import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Fragment } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { formatEnvironmentRange } from '/@/utils';
import type { ManagementHybrid } from './use-management-data';

/**
 * Recommended density by environment range, for hybrids with a data-backed table instead of
 * (or alongside) the yield/density curve. Ported from the legacy `CornDensityChartTable`
 * (`components/corn-density-chart-table`), adapted to `ManagementHybrid`'s shape.
 */
/** `order` isn't guaranteed unique on its own; the range it describes is. */
const entryKey = (entry: { minEnvironment?: number; maxEnvironment?: number; order?: number }) =>
    `${entry.minEnvironment ?? ''}-${entry.maxEnvironment ?? ''}-${entry.order ?? ''}`;

function DensityEnvironmentTable({ hybrids }: { hybrids: ManagementHybrid[] }) {
    const units = useUnitProvider();
    const rows = hybrids.filter((hybrid) => hybrid.densityTableValues.length > 0);

    if (!rows.length) return null;

    return (
        <Box sx={{ overflowX: 'auto' }}>
            {rows.map((hybrid) => {
                const values = hybrid.densityTableValues.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                return (
                    <Table key={hybrid.id} sx={{ minWidth: 650 }}>
                        <TableBody>
                            <Fragment>
                                <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                                    <TableCell rowSpan={3} align='center'>
                                        <Typography variant='body2' fontWeight={700} color='primary'>
                                            {hybrid.catalogName}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    {values.map((entry) => (
                                        <TableCell key={entryKey(entry)} align='center'>
                                            {formatEnvironmentRange(
                                                { min: entry.minEnvironment, max: entry.maxEnvironment },
                                                (value: number) => units.toYield(value, 'corn', undefined)
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                <TableRow>
                                    {values.map((entry) => {
                                        const density = units.toCornDensity(entry.value, 2);
                                        return (
                                            <TableCell key={entryKey(entry)} align='center'>
                                                {density.value} {density.unit}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </Fragment>
                        </TableBody>
                    </Table>
                );
            })}
        </Box>
    );
}

export default DensityEnvironmentTable;
